import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { generateSafeUsername } from '../utils/profanityFilter';
import AppHeader from './AppHeader';
import Footer from './Footer';
import { getUserStreak, type UserStreak } from '../utils/gameResultsService';
import DailyResultsAnalytics from './DailyResultsAnalytics';
import ProfilePicture from './ProfilePicture';

interface LearningSession {
  id: string;
  level_id: string;
  problems_attempted: number;
  problems_correct: number;
  total_time_seconds: number;
  accuracy: number;
  average_time_per_problem: number;
  xp_earned: number;
  completed_at: string;
  session_data?: any;
}

interface UserStats {
  sessionsCompleted: number;
  totalXpEarned: number;
  bestTime: number;
  currentStreak: number;
}

// Helper function to generate weekly stats from daily results
const generateWeeklyStats = (dailyResults: Array<{id: string, completed_at: string}>) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const today = new Date();
  
  // Get the start of this week (Monday)
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to Monday=0
  startOfWeek.setDate(today.getDate() - daysToMonday);
  startOfWeek.setHours(0, 0, 0, 0);

  // Initialize weekly data
  const weeklyData = days.map((day, index) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + index);
    return {
      day,
      games: 0,
      date: date.toISOString().split('T')[0] // YYYY-MM-DD format
    };
  });

  // Count games for each day
  dailyResults.forEach(result => {
    const resultDate = new Date(result.completed_at).toISOString().split('T')[0];
    const dayData = weeklyData.find(d => d.date === resultDate);
    if (dayData) {
      dayData.games++;
    }
  });

  return weeklyData;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [recentSessions, setRecentSessions] = useState<LearningSession[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [streakData, setStreakData] = useState<UserStreak | null>(null);
  const [weeklyData, setWeeklyData] = useState<{day: string, games: number}[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics'>('overview');

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      console.log('Loading dashboard data for user:', user.id);
      console.log('User object:', { id: user.id, email: user.email, firstName: user.firstName });
      
      // Load recent learning sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('learning_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false })
        .limit(10);

      if (sessionsError) {
        console.error('Error loading sessions:', sessionsError);
      } else {
        console.log('Loaded sessions:', sessions?.length || 0);
        setRecentSessions(sessions || []);
      }

      // Load streak data and daily results in parallel
      const [dailyResultsResponse, streakResponse] = await Promise.all([
        supabase
          .from('daily_grid_results')
          .select('id, completed_at, game_type, challenge_date')
          .eq('user_id', user.id)
          .order('completed_at', { ascending: false }),
        getUserStreak(user.id)
      ]);

      const { data: dailyResults, error: dailyError } = dailyResultsResponse;
      
      if (dailyError) {
        console.error('Error loading daily results:', dailyError);
      } else {
        console.log('Loaded daily results:', dailyResults?.length || 0, dailyResults);
      }

      // Generate weekly data for the chart using daily results
      const weeklyStats = generateWeeklyStats(dailyResults || []);
      console.log('Generated weekly stats:', weeklyStats);
      setWeeklyData(weeklyStats);

      // Set streak data
      console.log('Streak data:', streakResponse);
      setStreakData(streakResponse);

      let calculatedStats = { sessionsCompleted: 0, totalXpEarned: 0, bestTime: 0, currentStreak: streakResponse?.current_streak || 0 };
      
      // Use daily results as primary data source for game count
      const totalDailyGames = dailyResults?.length || 0;
      
      if (sessions && sessions.length > 0) {
        const totalXp = sessions.reduce((sum, s) => sum + (s.xp_earned || 0), 0);
        const bestTime = Math.min(...sessions
          .filter(s => s.average_time_per_problem)
          .map(s => s.average_time_per_problem));

        calculatedStats = {
          sessionsCompleted: totalDailyGames, // Count from daily_grid_results
          totalXpEarned: totalXp,
          bestTime: bestTime !== Infinity ? Math.round(bestTime) : 0,
          currentStreak: streakResponse?.current_streak || 0
        };
        
        console.log('Calculated stats (with learning sessions):', calculatedStats);
        setUserStats(calculatedStats);
      } else if (totalDailyGames > 0) {
        // If no learning sessions but have daily results
        calculatedStats.sessionsCompleted = totalDailyGames;
        console.log('Calculated stats (daily results only):', calculatedStats);
        setUserStats(calculatedStats);
      } else {
        // No data found
        console.log('No sessions found, using defaults:', calculatedStats);
        setUserStats(calculatedStats);
      }


    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900 flex items-center justify-center">
        <div className="text-2xl text-gray-600 dark:text-gray-200">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-black dark:to-gray-900">
      <AppHeader />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white/60 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-lg border border-white/30 dark:border-gray-700/30">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-3">
              Welcome back, {user?.firstName}! 👋
            </h1>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
              user?.subscriptionTier === 'elite' 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white shadow-md'
                : user?.isSubscribed 
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md' 
                : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-md'
            }`}>
              {user?.subscriptionTier === 'elite' ? '⭐ Elite Member' : user?.isSubscribed ? '⭐ Premium Member' : '🆓 Free Account'}
            </div>
          </div>
        </div>

        {/* Simple Navigation */}
        <div className="mb-6 sm:mb-8">
          <nav className="flex space-x-1 bg-white/60 dark:bg-gray-900 backdrop-blur-sm rounded-lg p-1 shadow-lg border border-white/30">
            {[
              { key: 'overview', label: 'Dashboard', icon: '🎮' },
              { key: 'analytics', label: 'Analytics', icon: '📊' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-sm sm:text-base">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>


        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Daily Challenges - Enhanced for Light Mode */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-4 sm:p-6 text-white shadow-xl border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1">🔥 Daily Challenges</h2>
                  <p className="text-sm sm:text-base text-purple-100">New puzzles every day • Build your streak!</p>
                </div>
                <div className="text-3xl sm:text-4xl opacity-80 hidden sm:block">🏆</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <Link 
                  to="/sum-grid" 
                  className="bg-white/20 hover:bg-white/30 backdrop-blur rounded-lg p-4 transition-all hover:scale-105 border border-white/20"
                >
                  <div className="text-2xl mb-2">➕</div>
                  <h3 className="font-semibold mb-1">Sum Grid</h3>
                  <p className="text-sm text-purple-100">Fill the grid with perfect sums</p>
                </Link>
                <Link 
                  to="/multiplication-grid" 
                  className="bg-white/20 hover:bg-white/30 backdrop-blur rounded-lg p-4 transition-all hover:scale-105 border border-white/20"
                >
                  <div className="text-2xl mb-2">✖️</div>
                  <h3 className="font-semibold mb-1">Multiplication Grid</h3>
                  <p className="text-sm text-purple-100">Master multiplication patterns</p>
                </Link>
                <Link 
                  to="/combined-grid" 
                  className="bg-white/20 hover:bg-white/30 backdrop-blur rounded-lg p-4 transition-all hover:scale-105 border border-white/20"
                >
                  <div className="text-2xl mb-2">🎯</div>
                  <h3 className="font-semibold mb-1">Combined Grid</h3>
                  <p className="text-sm text-purple-100">Ultimate math challenge</p>
                </Link>
              </div>
            </div>

            {/* Practice Mode - Enhanced for Light Mode */}
            <div className="bg-white/70 dark:bg-gray-800 backdrop-blur-sm rounded-xl p-4 sm:p-6 shadow-lg border border-white/30 dark:border-gray-700/30">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">🎯 Practice Mode</h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Play without time pressure • Perfect your skills</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Link 
                  to="/sum-grid?mode=practice" 
                  className="bg-white/80 dark:bg-gray-700 border-2 border-blue-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 rounded-lg p-4 transition-all hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-md"
                >
                  <div className="text-2xl mb-2">➕</div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Sum Grid Practice</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">No daily limit</p>
                </Link>
                <Link 
                  to="/multiplication-grid?mode=practice" 
                  className="bg-white/80 dark:bg-gray-700 border-2 border-green-200 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-500 rounded-lg p-4 transition-all hover:bg-green-50 dark:hover:bg-green-900/20 hover:shadow-md"
                >
                  <div className="text-2xl mb-2">✖️</div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Multiplication Practice</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">No daily limit</p>
                </Link>
                <a 
                  href="/mental-math-speed" 
                  className="bg-white/80 dark:bg-gray-700 border-2 border-orange-200 dark:border-gray-600 hover:border-orange-400 dark:hover:border-orange-500 rounded-lg p-4 transition-all hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:shadow-md"
                >
                  <div className="text-2xl mb-2">⚡</div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Mental Math Speed</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Quick calculations</p>
                </a>
                <Link 
                  to="/puzzle-games" 
                  className="bg-white/80 dark:bg-gray-700 border-2 border-purple-200 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 rounded-lg p-4 transition-all hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:shadow-md"
                >
                  <div className="text-2xl mb-2">🧩</div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">More Puzzles</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Explore all games</p>
                </Link>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {/* Recent Daily Results */}
            <div className="bg-white/70 dark:bg-gray-800 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/30 dark:border-gray-700/30">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Recent Daily Results</h2>
                  <p className="text-gray-600 dark:text-gray-400">Last 25 game sessions</p>
                </div>
              </div>
              <DailyResultsAnalytics />
            </div>
          </div>
        )}

      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;